import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, usePrivySmartWallet, useSwapContext } from "@src/contexts";
import { InitTransferView } from "./InitTransferView";
import { PendingTransactionView } from "../PendingTransactionView";
import { SuccessView } from "../SuccessView";
import { ErrorView } from "../ErrorView";
import ERC20Abi from "../../contractAbis/ERC20.json";
import { BigNumber, ethers } from "ethers";
import {
  calculateGasCashback,
  humanReadableToWei,
  weiToHumanReadable,
} from "@src/utils";
import {
  CASH_TOKEN_ADDRESS_BY_CHAIN,
  contractAddresses,
  TRANSFER_FEE_IN_CASH,
} from "@src/config";
import { FeeCollectorAbi, SwapperTransferProxyAbi } from "@src/contractAbis";
import { useDebounce, useTokenBalance } from "@src/hooks";
import swapperConfig from "../../../swapper.config";
import {
  HistoryEntryAction,
  HistoryEntryStatus,
  RoutePath,
} from "@src/interfaces";
import { Navigation, PageLayout } from "@src/components";
import { BackArrowIcon, CloseIcon } from "@src/assets";
import { useNavigate } from "react-router-dom";

enum TransferState {
  Init = "Init",
  Pending = "Pending",
  Success = "Success",
  Error = "Error",
}

export type GasFees = {
  platformFee: string;
  networkFee: string;
  totalFee: string;
};

export const TransferView = () => {
  const navigate = useNavigate();
  const [txHash, setTxHash] = useState("");
  const [areFeesLoading, setAreFeesLoading] = useState(false);
  const [feesEstimationError, setFeesEstimationError] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [showBackButton, setShowBackButton] = useState(true);
  const [notEnoughCashForFees, setNotEnoughCashForFees] = useState(false);
  const { executeTransaction, getGasFee, smartWalletAddress } =
    usePrivySmartWallet();
  const { addTransactionToLocalHistory, fetchHistory } = useHistory();
  const [gasFees, setGasFees] = useState<GasFees | undefined>();
  const {
    transferReceiverAddress,
    setTransferReceiverAddress,
    transferTokenAmount,
    setTransferTokenAmount,
    cashToken,
    transferToken,
    fetchAllBalances,
    setTransferToken,
    chain,
  } = useSwapContext();

  useEffect(() => {
    if (!transferToken) {
      setTransferToken(cashToken);
    }
  }, [transferToken, cashToken, setTransferToken]);

  const [transferState, setTransferState] = useState(TransferState.Init);
  const [cashbackAmount, setCashbackAmount] = useState<string | undefined>();

  const cashBalance = useTokenBalance({ token: cashToken });
  useEffect(() => {
    if (gasFees?.totalFee) {
      let cashAmountToTransfer = "0";
      if (
        transferToken &&
        transferToken.address ===
          CASH_TOKEN_ADDRESS_BY_CHAIN[transferToken.chainId]
      ) {
        cashAmountToTransfer = transferTokenAmount;
      }
      if (
        Number(cashBalance.humanReadable) -
          Number(
            weiToHumanReadable({
              amount: gasFees?.totalFee,
              decimals: 6,
              precisionFractionalPlaces: 2,
            }),
          ) -
          Number(cashAmountToTransfer) <
        0
      ) {
        setNotEnoughCashForFees(true);
      } else {
        setNotEnoughCashForFees(false);
      }
    }
  }, [cashBalance, gasFees, transferTokenAmount, transferToken]);
  useEffect(() => {
    if (
      transferState === TransferState.Init ||
      transferState === TransferState.Pending
    ) {
      setShowCloseButton(false);
    } else {
      setShowCloseButton(true);
    }
    if (transferState === TransferState.Init) {
      setShowBackButton(true);
    } else {
      setShowBackButton(false);
    }
  }, [transferState]);

  const isReceiverAddressValid = useMemo(() => {
    if (transferReceiverAddress.length === 0) return true;
    return ethers.utils.isAddress(transferReceiverAddress);
  }, [transferReceiverAddress]);

  const calls = useMemo(() => {
    const callsToExecute: {
      to: `0x${string}`;
      value?: bigint;
      data?: `0x${string}`;
    }[] = [];
    if (
      !transferToken ||
      !transferReceiverAddress ||
      !transferTokenAmount ||
      Number(transferTokenAmount) === 0
    ) {
      return callsToExecute;
    }
    const receiver = isReceiverAddressValid
      ? transferReceiverAddress
      : contractAddresses[transferToken.chainId]?.FeeCollector;
    const erc20Interface = new ethers.utils.Interface(ERC20Abi);
    const feeCollectorInterface = new ethers.utils.Interface(FeeCollectorAbi);
    const swapperTransferProxyInterface = new ethers.utils.Interface(
      SwapperTransferProxyAbi,
    );

    let amountToTransfer = humanReadableToWei({
      amount: transferTokenAmount,
      decimals: transferToken?.decimals || 18,
    });
    if (
      transferToken.address ===
      CASH_TOKEN_ADDRESS_BY_CHAIN[transferToken.chainId]
    ) {
      if (
        BigInt(cashBalance.wei) - BigInt(TRANSFER_FEE_IN_CASH) <
        BigInt(amountToTransfer)
      )
        amountToTransfer = (
          BigInt(amountToTransfer) - BigInt(TRANSFER_FEE_IN_CASH)
        ).toString();
      if (Number(amountToTransfer) < 0) {
        amountToTransfer = "0";
      }
    }
    callsToExecute.push({
      to: CASH_TOKEN_ADDRESS_BY_CHAIN[transferToken.chainId] as `0x${string}`,
      value: 0n,
      data: erc20Interface.encodeFunctionData("approve", [
        contractAddresses[transferToken.chainId]?.FeeCollector,
        TRANSFER_FEE_IN_CASH,
      ]) as `0x${string}`,
    });
    callsToExecute.push({
      to: contractAddresses[transferToken.chainId]
        ?.FeeCollector as `0x${string}`,
      value: 0n,
      data: feeCollectorInterface.encodeFunctionData("receiveToken", [
        CASH_TOKEN_ADDRESS_BY_CHAIN[transferToken.chainId],
        TRANSFER_FEE_IN_CASH,
      ]) as `0x${string}`,
    });
    if (transferToken.address !== ethers.constants.AddressZero) {
      callsToExecute.push({
        to: transferToken.address as `0x${string}`,
        value: 0n,
        data: erc20Interface.encodeFunctionData("approve", [
          contractAddresses[transferToken.chainId]?.SwapperTransferProxy,
          amountToTransfer,
        ]) as `0x${string}`,
      });
    }
    callsToExecute.push({
      to: contractAddresses[transferToken.chainId]
        ?.SwapperTransferProxy as `0x${string}`,
      value:
        transferToken.address === ethers.constants.AddressZero
          ? BigInt(amountToTransfer)
          : 0n,
      data: swapperTransferProxyInterface.encodeFunctionData("transferTo", [
        receiver,
        transferToken.address,
        amountToTransfer,
      ]) as `0x${string}`,
    });
    return callsToExecute;
  }, [
    transferTokenAmount,
    transferReceiverAddress,
    isReceiverAddressValid,
    transferToken,
    cashBalance,
  ]);

  const fetchGasFees = useCallback(async () => {
    if (chain && transferState === TransferState.Init && calls.length > 0) {
      try {
        setFeesEstimationError(false);
        setAreFeesLoading(true);
        const smartWalletGasFee = await getGasFee(calls, chain.chainId);
        if (smartWalletGasFee) {
          setGasFees({
            platformFee: TRANSFER_FEE_IN_CASH,
            networkFee: smartWalletGasFee,
            totalFee: (
              BigInt(TRANSFER_FEE_IN_CASH) + BigInt(smartWalletGasFee)
            ).toString(),
          });
        }
      } catch (err) {
        console.error(err);
        setFeesEstimationError(true);
      } finally {
        setAreFeesLoading(false);
      }
    }
  }, [calls, chain, getGasFee, transferState]);

  const startFeesLoading = useCallback(() => {
    if (calls.length > 0) {
      setAreFeesLoading(true);
    }
  }, [calls]);

  const { debounce: fetchGasFeesDebounce } = useDebounce({
    delayMs: 400,
    instantExecution: startFeesLoading,
  });

  useEffect(() => {
    fetchGasFeesDebounce(fetchGasFees);
  }, [fetchGasFeesDebounce, fetchGasFees]);

  useEffect(() => {
    const timer = setInterval(() => fetchGasFees(), 15000);
    return () => clearInterval(timer);
  }, [fetchGasFees]);

  const transfer = useCallback(async () => {
    if (!transferToken) {
      return;
    }
    setTransferState(TransferState.Pending);
    try {
      const transactionHash = await executeTransaction(
        calls,
        transferToken.chainId,
      );
      if (transactionHash) {
        setTxHash(transactionHash);

        if (smartWalletAddress && gasFees?.networkFee) {
          // Get transaction receipt from hash
          const provider = new ethers.providers.JsonRpcProvider(
            chain?.publicRpcUrls[0],
          );
          const receipt = await provider.waitForTransaction(transactionHash);

          try {
            const processTxPayload = {
              transactionHash: transactionHash,
              blockchainId: chain?.chainId,
            };
            const response = await fetch(`${swapperConfig.apiUrl}/process-tx`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(processTxPayload),
            });

            if (!response.ok) {
              addTransactionToLocalHistory(
                {
                  txHash: transactionHash,
                  action: HistoryEntryAction.external_transfer,
                  priceUsd: "-",
                  tokenAddress: transferToken.address,
                  date: new Date(),
                  status: HistoryEntryStatus.pending,
                },
                smartWalletAddress,
              );

              console.error(`process-tx failed: ${response.statusText}`);
            } else {
              await fetchHistory();
            }
          } catch (error) {
            console.error("Error processing transaction:", error);
          }

          const feeCollectorAddress =
            contractAddresses[transferToken?.chainId]?.FeeCollector;

          if (feeCollectorAddress) {
            const gasCashbackResult = await calculateGasCashback(
              receipt,
              BigNumber.from(gasFees?.networkFee),
              CASH_TOKEN_ADDRESS_BY_CHAIN[transferToken.chainId],
              cashToken?.decimals || 6,
              smartWalletAddress,
              [feeCollectorAddress, transferReceiverAddress],
            );

            if (gasCashbackResult.cashbackAmount.gt(0)) {
              setCashbackAmount(
                weiToHumanReadable({
                  amount: gasCashbackResult.cashbackAmount.toString(),
                  decimals: cashToken?.decimals || 6,
                  precisionFractionalPlaces: 4,
                }),
              );
            }
          }
        }
        setTransferState(TransferState.Success);
        fetchAllBalances();
      } else {
        setTransferState(TransferState.Error);
      }
    } catch (err) {
      setTransferState(TransferState.Error);
    }
  }, [
    transferToken,
    executeTransaction,
    calls,
    smartWalletAddress,
    gasFees?.networkFee,
    cashToken?.decimals,
    transferReceiverAddress,
    fetchAllBalances,
    chain,
    addTransactionToLocalHistory,
    fetchHistory,
  ]);

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: showBackButton && <BackArrowIcon />,
              path: RoutePath.Wallet,
            },
            {
              content: "Transfer",
              disabled: true,
              path: RoutePath.Transfer,
            },
          ]}
          rightItems={
            showCloseButton
              ? [
                  {
                    content: <CloseIcon />,
                    onClick: () => navigate(RoutePath.Wallet),
                  },
                ]
              : []
          }
        />
      }
    >
      <>
        {transferState === TransferState.Init && (
          <InitTransferView
            amount={transferTokenAmount}
            setAmount={setTransferTokenAmount}
            receiverAddress={transferReceiverAddress}
            setReceiverAddress={setTransferReceiverAddress}
            onTransfer={transfer}
            chain={chain}
            token={transferToken}
            cashToken={cashToken}
            areFeesLoading={areFeesLoading}
            gasFees={gasFees}
            notEnoughCashForFees={notEnoughCashForFees}
            feesEstimationError={feesEstimationError}
          />
        )}
        {transferState === TransferState.Pending && (
          <PendingTransactionView
            type="transfer"
            fromAmount={transferTokenAmount}
            receiverAddress={transferReceiverAddress}
            token={transferToken}
          />
        )}
        {transferState === TransferState.Success && (
          <SuccessView
            title={"Transfer successful"}
            amount={transferTokenAmount}
            cashbackAmount={cashbackAmount}
            token={transferToken}
            forceUSDC={true}
            txHash={txHash}
            explorerUrl={chain?.transactionExplorer}
          />
        )}
        {transferState === TransferState.Error && (
          <ErrorView title={"Transfer unsuccessful"} />
        )}
      </>
    </PageLayout>
  );
};
